import { QueryClient, QueryClientProvider, useQueryClient } from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { useEffect } from 'react'
import type { ReactNode } from 'react'
import { subscribeToChannelConnectionChanged } from '@/lib/channel-connection-events'
import { queryKeys } from './keys'
import { useWebSocketSync } from './shared/useWebSocketSync'

// Create a client with default options
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 2, // 2 minutes - data is fresh for 2 minutes
      gcTime: 1000 * 60 * 10, // 10 minutes - cache persists for 10 minutes (formerly cacheTime)
      retry: (failureCount, error: unknown) => {
        // Avoid retry storms during backend instability.
        if (failureCount >= 1) return false
        const status = (error as { status?: number } | undefined)?.status
        return typeof status === 'number' && status >= 500
      },
      refetchOnWindowFocus: false, // Don't refetch on window focus
      refetchOnReconnect: true, // Refetch when network reconnects
    },
    mutations: {
      retry: 0, // Don't retry mutations
    },
  },
})

interface QueryProviderProps {
  children: ReactNode
}

/**
 * Internal component that handles WebSocket sync
 * Separated to ensure it only runs when QueryClient is available
 */
function WebSocketSync() {
  useWebSocketSync()
  return null
}

function ChannelConnectionQuerySync() {
  const queryClient = useQueryClient()

  useEffect(() => {
    const unsubscribe = subscribeToChannelConnectionChanged((detail) => {
      if (detail.channel === 'whatsapp') {
        queryClient.invalidateQueries({ queryKey: queryKeys.meta.whatsappStatus() })
      }

      if (detail.channel === 'facebook_ads') {
        queryClient.invalidateQueries({ queryKey: queryKeys.meta.facebookStatus() })
      }
    })

    return unsubscribe
  }, [queryClient])

  return null
}

export function QueryProvider({ children }: QueryProviderProps) {
  return (
    <QueryClientProvider client={queryClient}>
      <WebSocketSync />
      <ChannelConnectionQuerySync />
      {children}
      {process.env.NODE_ENV === 'development' && (
        <ReactQueryDevtools initialIsOpen={false} />
      )}
    </QueryClientProvider>
  )
}
